import * as React from 'react';

import { cn } from '@/utils/cn';
import { cva, type VariantProps } from 'class-variance-authority';

const inputVariants = cva(
  'w-full border border-solid rounded-md transition-colors focus-visible:outline-none disabled:cursor-not-allowed appearance-none',
  {
    variants: {
      variant: {
        default:
          'bg-background-50 border-border-100 text-typography-900 placeholder:text-typography-900/25 hover:border-border-200 focus-visible:border-primary focus-visible:shadow-secondary-100 disabled:bg-background disabled:text-typography-900/40 disabled:border-border',
        borderless:
          'border-none rounded-none bg-transparent focus-visible:shadow-none text-typography-900 placeholder:text-typography-900/25'
      },
      size: {
        sm: 'h-6 px-2 py-0 text-sm leading-[22px]',
        md: 'h-8 px-3 py-1 text-[14px] leading-[22px]',
        lg: 'h-10 px-3 py-[7px] text-base leading-[24px]'
      },
      status: {
        default: '',
        error: 'border-error-500 hover:border-error-600 focus-visible:border-error-400 focus-visible:shadow-error-100',
        warning:
          'border-warning-500 hover:border-warning-600 focus-visible:border-warning-500 focus-visible:shadow-warning-100',
        success: 'border-success hover:border-success-600 focus-visible:border-success focus-visible:shadow-success-100'
      }
    },
    compoundVariants: [
      {
        variant: 'borderless',
        status: ['error', 'warning', 'success'],
        className: 'border-none shadow-none'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      status: 'default'
    }
  }
);

export type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & VariantProps<typeof inputVariants>;

const Input = ({ className, type = 'text', variant, size, status, ...props }: InputProps) => {
  return <input type={type} className={cn(inputVariants({ variant, size, status, className }))} {...props} />;
};
Input.displayName = 'Input';

export { Input };
